import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import AuthShell, { AuthField, authInputClass } from "@/components/AuthShell";
import { useAuth } from "@/lib/auth";

/** Shown once, on a brand-new install, to create the first admin account. */
export default function Setup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;
  const ready = form.full_name && form.email && form.password.length >= 8 && !mismatch;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { user } = await api.auth.setup({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      });
      setUser(user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Set up App Hub"
      subtitle="Create the first admin account. You can invite everyone else afterwards."
    >
      <form onSubmit={submit} className="space-y-4">
        <AuthField label="Your name">
          <input value={form.full_name} onChange={set("full_name")} className={authInputClass} autoFocus required />
        </AuthField>

        <AuthField label="Email">
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            className={authInputClass}
            placeholder="you@goldstarprinting.us"
            autoComplete="username"
            required
          />
        </AuthField>

        <AuthField label="Password" hint="At least 8 characters.">
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            className={authInputClass}
            autoComplete="new-password"
            required
          />
        </AuthField>

        <AuthField label="Confirm password">
          <input
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            className={authInputClass}
            autoComplete="new-password"
            required
          />
        </AuthField>

        {mismatch && <p className="text-amber-300 text-sm">Those passwords don't match.</p>}
        {error && <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}

        <button
          type="submit"
          disabled={busy || !ready}
          className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:hover:bg-purple-600 text-white rounded-xl py-2.5 text-sm font-medium transition-colors"
        >
          {busy ? "Creating…" : "Create admin account"}
        </button>
      </form>
    </AuthShell>
  );
}
